import React, { useState, createContext, useContext } from 'react'
import PropTypes from 'prop-types'
import { RenderIf } from '../../../../utils/fns'
import { ACCORDION_TYPE } from '../../common/constants'
import '../../../../globals/scss/components/accordion.scss'

const AccordionContext = createContext(null)

const AccordionRoot = ({
  children,
  type = ACCORDION_TYPE.SINGLE,
  value,
  onValueChange,
  className,
}) => {
  const isMultiple = type === ACCORDION_TYPE.MULTIPLE

  const handleToggle = (itemValue) => {
    if (!onValueChange) return

    if (isMultiple) {
      const currentValues = Array.isArray(value) ? value : []
      const updatedValues = currentValues.includes(itemValue)
        ? currentValues.filter((v) => v !== itemValue)
        : [...currentValues, itemValue]

      onValueChange(updatedValues)
    } else {
      onValueChange(value === itemValue ? undefined : itemValue)
    }
  }

  const accordionContextValue = {
    value,
    isMultiple,
    handleToggle,
  }

  return (
    <AccordionContext.Provider value={accordionContextValue}>
      <div className={`accordion-group ${className}`}>{children}</div>
    </AccordionContext.Provider>
  )
}

const AccordionItem = ({ value, title, icon, children, className, ...props }) => {
  const { value: activeValue, isMultiple, handleToggle } = useContext(AccordionContext)

  const isOpen = isMultiple
    ? activeValue.includes(value)
    : value === activeValue

  return (
    <div className={`accordion ${isOpen ? '-open' : ''} ${className ?? ''}`} {...props}>
      <h3 className="accordion__header" onClick={() => handleToggle(value)}>
        {RenderIf(
          icon,
          <span className="accordion__headerImg">
            <img className="ui avatar image" src={icon} alt="accordion's icon" />
          </span>,
        )}
        <span className="accordion__headerTitle">{title}</span>
        <span className="material-icons accordion__headerCaret">keyboard_arrow_right</span>
      </h3>

      <div className="accordion__body">{children}</div>
    </div>
  )
}

const Accordion = ({ title, icon, children, className, defaultOpen = true, value, ...props }) => {
  const [selectedValue, setSelectedValue] = useState(defaultOpen ? value : undefined)

  return (
    <AccordionRoot value={selectedValue} onValueChange={setSelectedValue} className={className}>
      <AccordionItem value={value} title={title} icon={icon} {...props}>
        {children}
      </AccordionItem>
    </AccordionRoot>
  )
}

const nodeOrNodes = PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)])

AccordionRoot.propTypes = {
  children: nodeOrNodes.isRequired,
  type: PropTypes.oneOf([ACCORDION_TYPE.SINGLE, ACCORDION_TYPE.MULTIPLE]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onValueChange: PropTypes.func,
  className: PropTypes.string,
}

AccordionItem.propTypes = {
  value: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, nodeOrNodes]).isRequired,
  icon: PropTypes.string,
  children: nodeOrNodes.isRequired,
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
}

Accordion.propTypes = {
  value: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, nodeOrNodes]).isRequired,
  icon: PropTypes.string,
  children: nodeOrNodes.isRequired,
  className: PropTypes.string,
  defaultOpen: PropTypes.bool,
}

export default Object.assign(Accordion, {
  Root: AccordionRoot,
  Item: AccordionItem,
})
